// tslint:disable: no-unused-expression
import { HttpEvent, HttpEventType, HttpErrorResponse } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// ENVIRONMENT
import { environment } from 'src/environments/environment';

// SERVICIOS
import { AsynchronousProgressService } from '../TemplateServices/asynchronous-progress.service';
import { UserInformationService } from '../TemplateServices/user-information.service';
import { SharedFunctionsService } from '../TemplateServices/shared-functions.service';
import { NetworkIndicatorsService } from './network-indicators.service';
import { BoardOfDirectorsService } from './board-of-directors.service';
import { ExternalRequestsService } from './external-requests.service';
import { ContactCenterService } from "./contact-center.service";
import { ConsultancyService } from './consultancy.service';
import { ManagmentService } from './managment.service';
import { DatatableService } from './datatable.service';
import { ReportsService } from './reports.service';
import { EntityService } from './entity.service';
import { RisksService } from './risks.service';
import { UserService } from "./user.service";

// CONSTANTES
import { BOARDOFDIRECTORS } from 'src/app/constants/board-of-directors';
import { PROGRESSVALUES } from 'src/app/constants/progress-values';
import { CONTACTCENTER } from 'src/app/constants/contact-center';
import { NETWORKS } from 'src/app/constants/network-indicators';
import { EXTERNAL } from 'src/app/constants/external-services';
import { MANAGEMENTSVE } from 'src/app/constants/management';
import { CONSULTANCY } from 'src/app/constants/consultancy';
import { REPORTS } from 'src/app/constants/reports';
import { ENTITY } from 'src/app/constants/entity';
import { RISKS } from 'src/app/constants/risks';

// INTERFACES
import { StringValues, AnyValues } from '../../interfaces/undetermined';

@Injectable({ providedIn: 'root' })
export class CallApiMethodsService {

  // returnValues: Almacena el % de progreso que se retornará a la barra de progreso, por cada una de las peticiones
  returnValues: number[] = [];
  // replaceKey: Copia la interfaz con la que se evaluarán los parametros para que el nombre de las llaves coincida con el back
  replaceKey: StringValues;

  constructor(
    private asynchronousProgressService: AsynchronousProgressService,
    private networkIndicatorsService: NetworkIndicatorsService,
    private externalRequestsService: ExternalRequestsService,
    private contactCenterService: ContactCenterService,
    private boardOfDirectors: BoardOfDirectorsService,
    private consultancyService: ConsultancyService,
    private sharedMethods: SharedFunctionsService,
    private managmentService: ManagmentService,
    private datatableService: DatatableService,
    private reportService: ReportsService,
    private entityService: EntityService,
    private user: UserInformationService,
    private riskService: RisksService,
    private userService: UserService,
    private router: Router
  ) { }

  /* sendAsynchronousRequest(): Método que devuelve la respuesta del API cuando finaliza o envía a la página de error cuando falla
    service: Nombre del servicio que se ejecutará
    method: Método del servicio a llamar
    params: Parámetros que se enviarán al API
    path: Cadena que se agregará a la url del API
  */
  sendAsynchronousRequest(service: string, method: string, params?: AnyValues, path?: string, loader = true, timing = true): Promise<any> {
    const userStorage: string = localStorage.getItem("user");
    if (userStorage && this.sharedMethods.checkTime(JSON.parse(userStorage).time ? JSON.parse(userStorage).time : 0)) {
      if (timing) { this.sharedMethods.updateTime(); }
      // ↓ Devuelve una promesa con la información del servicio
      return new Promise((resolve: (data: any) => void, reject: (data: HttpErrorResponse) => void) => {
        this.callMethod(service, method, params, path, loader).subscribe((data: any[]) => {
          // ↓ Espera la respuesta y resuelve la promesa
          if (data.length > 1) { resolve(data[1]); }
        }, (data: [null, HttpErrorResponse]) => reject(data[1]));
        // ↑ Si falla el servicio rechaza la promesa
      });
    } else { this.sharedMethods.redirectToLogInPage(); }
  }

  /* callMethod(): Método que se encarga de hacer las peticiones a la API, recibe
    service: Nombre del servicio que se ejecutará
    method: Método del servicio a llamar
    body: Parámetros que se enviarán al API
    path: Cadena que se agregará a la url del API
  */
  callMethod(service: string, method: string, body?: any, path?: string, loader?: boolean): Observable<any> {
    if (loader) {
      // ↓ Añade petición asíncrona al servicios de barra de progreso
      this.asynchronousProgressService.goal += 100;
      // ↓ genera el % de progreso aleatorio entre 5 valores
      this.returnValues = this.returnValues.concat(PROGRESSVALUES.sort(() => Math.random() - 0.5));
    }
    // Define el método a ejecutar por servicio de acuerdo a los parámetros recibidos
    switch (service) {
      case 'users':
        // tokens: Tokens para las consultas get
        const tokens = `user_token=${this.user.userToken}&platform_token=${this.user.platform}&page_token=${this.user.currentPage || "59"}`;
        switch (method) {
          // Se agregan las credenciales de usuario a la petición GET
          case 'getEntityMenu':
            return this.userService.getMenu(`?${tokens}&entity=${this.user.entityId}`).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "GET", loader)),
              catchError(this.emitError),
            );
          case 'getAlertPermissions':
            return this.userService.getAlertPermissions(`?${tokens}`).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "GET", loader)),
              catchError(this.emitError),
            );
          case 'getModules':
            return this.userService.getModules(`?${tokens}`).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "GET", loader)),
              catchError(this.emitError),
            );
          case 'getMenu':
            return this.userService.getMenu(`?${tokens}`).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "GET", loader)),
              catchError(this.emitError),
            );
          case 'logOut':
            return this.userService.logOut(`?${tokens}`).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "GET", loader)),
              catchError(this.emitError),
            );
          case 'logIn':
            return this.userService.logIn(`?${tokens}`).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "GET", loader)),
              catchError(this.emitError),
            );
        }
        break;
      case 'contactCenter':
        // ↓ Define el formateador para los parámetros
        this.replaceKey = Object.assign({}, CONTACTCENTER);
        // ↓ Formatea los parámetros para cada petición POST
        body = this.setData(body);
        switch (method) {
          case 'getCPCReport':
            return this.contactCenterService.getCPCReport(body as FormData, "getCollectionInPaymentChannelsReport").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getOperations':
            return this.contactCenterService.getContactCenterOperations(body as FormData).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getRecoveries':
            return this.contactCenterService.getContactCenterRecoveries(body as FormData).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getReports':
            return this.contactCenterService.getContactCenterReports(body as FormData).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getRefunds':
            return this.contactCenterService.getContactCenterRefunds(body as FormData).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'importRefunds':
            return this.contactCenterService.importMassiveRefunds(body as FormData).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
        }
        break;
      case 'externalRequests':
        // ↓ Define el formateador para los parámetros
        this.replaceKey = Object.assign({}, EXTERNAL);
        // ↓ Formatea los parámetros para cada petición POST
        body = this.setData(body);
        switch (method) {
          case 'getFromREP':
            return this.externalRequestsService.getInformationFromRepotencia(path, body as FormData).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getFromRisk':
            return this.externalRequestsService.getInformationFromRisk(body as FormData).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getFromGCM':
            return this.externalRequestsService.getInformationFromGCM(body as FormData).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getFromBdr':
            return this.externalRequestsService.getInformationFromBdr(body as FormData).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'updateRecoveriesLastMonth':
            return this.externalRequestsService.updateRecoveriesLastMonth().pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "GET", loader)),
              catchError(this.emitError),
            );
          case 'updateIRL':
            return this.externalRequestsService.updateIRL().pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "GET", loader)),
              catchError(this.emitError),
            );
          case 'getFromBloomrisk':
            return this.externalRequestsService.getInformationFromBloomrisk(path, body as FormData).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
        }
        break;
      case 'networkIndicators':
        // ↓ Define el formateador para los parámetros
        this.replaceKey = Object.assign({}, NETWORKS);
        // ↓ Formatea los parámetros para cada petición POST
        body = this.setData(body);
        switch (method) {
          case 'getFromTechnologicalNetwork':
            return this.networkIndicatorsService.getIndicatorsTechnologicalNetwork(body as FormData, "getInformationFromTechnologicalNetworkIndicators").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getFromMarketingNetwork':
            return this.networkIndicatorsService.getIndicatorsMarketingNetwork(body as FormData, "getInformationFromMarketingNetworkIndicators").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getFromFinancialNetwork':
            return this.networkIndicatorsService.getIndicatorsFinancialNetwork(body as FormData, "getInformationFromFinancialNetworkIndicators").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getFinancialNetworkIndicatorsFromBdr':
            return this.networkIndicatorsService.getFinancialNetworkIndicatorsBdr(body as FormData, "getFinancialNetworkIndicatorsFromBdr").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getFromLegalNetwork':
            return this.networkIndicatorsService.getIndicatorsLegalNetwork(body as FormData, "getInformationFromLegalNetworkIndicators").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getFromWebService':
            return this.networkIndicatorsService.getIndicatorsLegalNetwork(body as FormData, "getIndicatorsFromWebService").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getClaimIndicators':
            return this.networkIndicatorsService.getFinancialNetworkIndicatorsBdr(body as FormData, "getClaimIndicators").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
        }
        break;
      case 'management':
        // ↓ Define el formateador para los parámetros
        this.replaceKey = Object.assign({}, MANAGEMENTSVE);
        // ↓ Formatea los parámetros para cada petición POST
        body = this.setData(body);
        switch (method) {
          case 'proccessBoardOfDirectors':
            return this.managmentService.proccessBoardOfDirectors(body as FormData, "proccessBoardOfDirectors").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'proccessStrategicMap':
            return this.managmentService.proccessStrategicMap(body as FormData, "proccessStrategicMap").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getRoutes':
            return this.managmentService.getRoutes(body as FormData, "getRouteInformation").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getUsers':
            return this.managmentService.getUsers(body as FormData, "getUserInformation").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'buildReports':
            return this.managmentService.buildReports(body as FormData, "buildReports").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getEntities':
            return this.managmentService.getEntities(body as FormData, "getEntities").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'manageLog':
            return this.managmentService.manageLog(body as FormData, "manageLog").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
        }
        break;
      case 'boardOfDirectors':
        // ↓ Define el formateador para los parámetros
        this.replaceKey = Object.assign({}, BOARDOFDIRECTORS);
        // ↓ Formatea los parámetros para cada petición POST
        body = this.setData(body);
        switch (method) {
          case 'getFromBoardOfDirectors':
            return this.boardOfDirectors.getInformationFromBoardOfDirectors(body as FormData, "getInformationFromBoardOfDirectors").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getFromHarvest':
            return this.boardOfDirectors.getHarvestIndicators(body as FormData, "getHarvestIndicators").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getProcessedData':
            return this.boardOfDirectors.getProcessedData(body as FormData, "getIndicatorsFromRFile").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
        }
        break;
      case 'entity':
        // ↓ Define el formateador para los parámetros
        this.replaceKey = Object.assign({}, ENTITY);
        // ↓ Formatea los parámetros para cada petición POST
        body = this.setData(body);
        // ↓ Añade las credenciales de la entidad para la petición POST
        (body as FormData).append("entity_token", this.user.entityId);
        switch (method) {
          case 'getIndicators':
            return this.entityService.getEntityIndicators(body as FormData, "getEntityIndicators").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getMobilized':
            return this.entityService.getMobilized(body as FormData, "getMoneyMobilized").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getAgreements':
            return this.entityService.getAgreements(body as FormData, "getAgreements").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getClaims':
            return this.entityService.getClaims(body as FormData, "getClaims").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
        }
        break;
      case 'reports':
        // ↓ Define el formateador para los parámetros
        this.replaceKey = Object.assign({}, REPORTS);
        // ↓ Formatea los parámetros para cada petición POST
        body = this.setData(body);
        switch (method) {
          case 'getPAEReport':
            return this.reportService.getPAEReport(body as FormData, "getProfitabilityOfAgreementsAndEntitiesReport").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getMRPReport':
            return this.reportService.getMRPReport(body as FormData, "getManageAndRecoveryOfPortfolioReport").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getMADReport':
            return this.reportService.getMADReport(body as FormData, "getMobilizedAndDisbursementsReport").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getCUPReport':
            return this.reportService.getCUPReport(body as FormData, "getCoverageUnitPerformanceReport").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getMGReport':
            return this.reportService.getMGReport(body as FormData, "getMobilizedGrowthReport").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'getCAOReport':
            return this.reportService.getCAOReport(body as FormData, "getClaimsReport").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
          case 'saveMRPReport':
            return this.reportService.saveMRPReport(body as FormData, "savePDFFiles").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
        }
        break;
      case 'datatable':
        // ↓ Define el formateador para los parámetros
        this.replaceKey = null;
        // ↓ Formatea los parámetros para cada petición POST
        body = this.setData(body);
        switch (method) {
          case 'getFields':
            return this.datatableService.getFields(path, body as FormData).pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
        }
        break;
      case 'risks':
        // ↓ Define el formateador para los parámetros
        this.replaceKey = Object.assign({}, RISKS);
        // ↓ Formatea los parámetros para cada petición POST
        body = this.setData(body);
        switch (method) {
          case 'getIRL':
            return this.riskService.getIRLInfo(body as FormData, "getIRLInformation").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
        }
        break;
      case 'consultancy':
        // ↓ Define el formateador para los parámetros
        this.replaceKey = Object.assign({}, CONSULTANCY);
        // ↓ Formatea los parámetros para cada petición POST
        body = this.setData(body);
        switch (method) {
          case 'getConsultancyReport':
            return this.consultancyService.getConsultancyReport(body as FormData, "getConsultancyReport").pipe(
              map((data: HttpEvent<any>) => this.getAnswer(data, "POST", loader)),
              catchError(this.emitError),
            );
        }
        break;
    }
  }

  /* getAnswer(): Método que obtiene la trazabilidad de la petición y por cada tipo de evento envía parte del progreso
    event: Retorno de la petición por cada tipo de evento
    method: tipo de solicitud para saber por cuantos dipos de progreso entrará (POST)->5, (GET)->4
  */
  getAnswer = (event: HttpEvent<any>, method: string, loader: boolean): any[] => {
    // arrayToReturn: Guarda el próximo valor de progreso a enviar.
    let arrayToReturn: any[];
    if (loader) {
      switch (event.type) {
        // Paso 1: Solicitud de envío
        case HttpEventType.Sent:
          // ↓ Selecciona el valor a enviar
          arrayToReturn = [this.returnValues[0]];
          // ↓ Elimina del array el valor que se enviará
          this.returnValues.shift();
          break;
        // Paso 2: Proceso de carga
        case HttpEventType.UploadProgress:
          // ↓ Selecciona el valor a enviar
          arrayToReturn = [this.returnValues[0]];
          // ↓ Elimina del array el valor que se enviará
          this.returnValues.shift();
          break;
        // Paso 3: Respuesta de CORS
        case HttpEventType.ResponseHeader:
          // ↓ Selecciona el valor a enviar
          arrayToReturn = [this.returnValues[0]];
          // ↓ Elimina del array el valor que se enviará
          this.returnValues.shift();
          break;
        // Paso 4: Proceso de descarga (solo POST)
        case HttpEventType.DownloadProgress: arrayToReturn = [0]; break;
        // Paso 5: Respuesta del servidor
        case HttpEventType.Response:
          // ↓ Selecciona el valor a enviar
          arrayToReturn = [this.returnValues[0] + this.returnValues[this.returnValues.length - 1]];
          // ↓ Elimina del array el valor que se enviará
          this.returnValues.shift(); this.returnValues.pop();
          // Cuando es tipo GET se ejecutan 5 iteraciones en vez de 4, por eso se suma al siguiente valor
          if (method === "GET") {
            arrayToReturn[0] += this.returnValues[this.returnValues.length - 1];
            this.returnValues.pop();
          }
          // ↓ Añade la respuesta del API
          arrayToReturn.push(event.body);
          break;
      }
      // ↓ Envía el progreso al servicio de barra de carga
      this.asynchronousProgressService.addResult(arrayToReturn[0] ? arrayToReturn[0] : 0);
      // ↓ Resetea el valor a enviar
      arrayToReturn[0] = null;
    } else {
      arrayToReturn = event.type === HttpEventType.Response ? [null, event.body] : [];
    }
    // Devuelva la respuesta del API
    return arrayToReturn;
  }

  /* emitError() Método que se ejecuta cuando ocurre un error en la solicitud
    error: Información del error, retornada por el API
  */
  emitError = (error: HttpErrorResponse): Observable<[null, HttpErrorResponse]> => {
    // ↓ Verifica si hay más solicitudes en ejecución para finaliza la barra de progreso
    if (this.returnValues.length > 0) {
      // ↓ Envía el progreso al servicio de barra de carga
      this.asynchronousProgressService.addResult(this.returnValues.reduce((bef, curr) => bef + curr));
      // ↓ Resetea el array
      this.returnValues = [];
    }
    return throwError([null, error]);
  }

  /* setData(): Estandariza la información para los métodos de acuerdo a los parámetros
    data: Parámetros de la solicitud
  */
  setData(data: any): FormData {
    // ↓ Verifica que no esté vacío
    if (data) {
      // body: se define como formulario para enviar facilmente
      const body: FormData = new FormData();
      // posted: Convierte objeto (key:value) a un array (key, value)
      const posted: [string, string][] = Object.entries(data);
      posted.forEach(element => {
        // define los parametros que se enviarán para recibir la información de junta directiva
        if (typeof element[1] === 'object') {
          if (element[0] === "file") {
            // agrega información al formulario según el tipo de variable recibida
            body.append(this.replaceKey ? this.replaceKey[element[0]] : element[0], element[1]);
          } else {
            // Verifica si el valor es un objeto
            if (typeof element[1] === 'object') {
              // Si es un objeto se organizan los parámetros de la forma key[key2], val
              Object.keys(element[1]).forEach((elm) => {
                body.append(`${this.replaceKey ? this.replaceKey[element[0]] : element[0]}[${this.replaceKey ? (this.replaceKey[elm] ? this.replaceKey[elm] : []) : (elm ? elm : [])}]`, element[1][elm]);
              });
            } else {
              // Si no es un objeto se organizan los parámetros de la forma key, JSON(val)
              body.append(this.replaceKey ? this.replaceKey[element[0]] : element[0], JSON.stringify(element[1]));
            }
          }
        } else {
          body.append(this.replaceKey ? this.replaceKey[element[0]] : element[0], element[1].toString());
        }
      });
      // ↓↓↓ Añade las credenciales del usuario, la plataforma y el id de la página a la solicitud POST
      body.append("page_token", this.user.currentPage || "59");
      body.append("platform_token", this.user.platform);
      body.append("user_token", this.user.userToken);
      // ↓ Retorna los parametros en formato FormData para enviarlos por solicitud POST
      return body;
    }
  }

  /* setError(): Método que redirecciona a la página de error cuando un proceso asíncrono falla
    error: Información del error
  */
  setError = (error: HttpErrorResponse) => {
    // ↓ Guarda mensaje de error en el log
    this.sendAsynchronousRequest("management", "manageLog", { operation: 1, user: this.user.userId, description: `Error Front - ${error.message}`, module: this.user.currentPage, event: "get", type: 1 });
    // ↓ Verifica si hay más solicitudes en ejecución para finaliza la barra de progreso
    if (this.returnValues.length > 0) {
      // ↓ Envía el progreso al servicio de barra de carga
      this.asynchronousProgressService.addResult(this.returnValues.reduce((bef, curr) => bef + curr));
      // ↓ Resetea el array
      this.returnValues = [];
    }
    !environment.production ? console.error(error.message || error) : (this.user.userId === "aospina" ? (console.error(error.message || error)) : null);
    // error: guarda el mensaje de error sin url
    const message = `${error.message.split(error.url)[0]} ${error.name}`;
    // status: guarda el estado de la solicitud (200,404,...)
    const status = error.status;
    // ↓↓ Actualizan el localstorage para guardar la información del error y poder acceder desde cualquier página
    localStorage.setItem("error", message);
    localStorage.setItem("status", `${status}`);
    // ↓ Direcciona a la página del error
    this.router.navigate(["error"]);
  }

}
